import { ACTION_TYPE, AppContext } from 'context/appContext';
import {
  Outlet,
  useLoaderData,
  useNavigation,
  useLocation,
} from 'react-router';
import {
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import AuthService from 'utils/AuthService';
import { useTranslation } from 'react-i18next';
import {
  PropertyGroupListItem,
  PropertyListItem,
  FormMode,
  Tenant,
  PanelRootContext,
} from 'types';
import { getTenantPropertiesList, getTenantPropertyGroupsList } from 'api';
import { ROUTES } from 'router/routes';
import { routeWithParams } from 'router/routes';
import { VerticalTabLayout } from 'components/layout/VerticalTabLayout';

export interface DashboardLoaderParams {
  params: {
    tenantID?: string;
    dateRange?: string;
  };
}
export interface DashboardLoaderData {
  properties: PropertyListItem[];
  propertyGroups: PropertyGroupListItem[];
}

export type DashboardTabs =
  | 'activity-feed'
  | 'people'
  | 'company-setup'
  | 'jobs'
  | 'properties'
  | 'property-groups'
  | 'permissions'
  | 'rule-config'
  | 'tenant-select'
  | 'command-center'
  | 'reports'
  | 'user-profile';

const dashboardLoader = async ({
  params,
}: DashboardLoaderParams): Promise<DashboardLoaderData> => {
  console.log('dashboardLoader');
  const tenantID = Number(params.tenantID) || 0;

  const properties = await getTenantPropertiesList(tenantID);
  const propertyGroups = await getTenantPropertyGroupsList(tenantID, true);

  return {
    properties: properties,
    propertyGroups: propertyGroups || [],
  } satisfies DashboardLoaderData;
};

const Component = (): ReactNode => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { properties, propertyGroups } = useLoaderData() as DashboardLoaderData;
  const { state, dispatch } = useContext(AppContext);
  const location = useLocation();
  const [tenant, setTenant] = useState<Tenant>(state.tenant);

  const [formMode, setFormMode] = useState<FormMode>(
    location?.state?.formMode || 'none',
  );

  // header and breadcrumbs
  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('dashboard'),
    });
    dispatch({
      type: ACTION_TYPE.SET_ENTITY_NAME,
      payload: state.tenant.name || '',
    });
    return () => {
      dispatch({
        type: ACTION_TYPE.SET_ENTITY_NAME,
        payload: '',
      });
    };
  }, [state.tenant.name]);

  // permissions
  const allowedTabs: DashboardTabs[] = useMemo(() => {
    const tenantTabs: DashboardTabs[] = [
      'jobs',
      'activity-feed',
      'properties',
      'property-groups',
      'tenant-select',
      'company-setup',
      'permissions',
      'people',
      'rule-config',
      'command-center',
      'reports',
      'user-profile',
    ];
    if (AuthService.IsSuperAdmin()) {
      return tenantTabs;
    }

    return tenantTabs.filter((tab) => {
      switch (tab) {
        case 'activity-feed':
        case 'reports':
        case 'user-profile':
          return true;
        case 'rule-config':
          return !AuthService.IsPM() && state.entityPermissions.rule.read;
        case 'jobs':
          return state.entityPermissions.job.read;
        case 'properties':
          return state.entityPermissions.property.read;
        case 'property-groups':
          return state.entityPermissions.property_group.read;
        case 'permissions':
          return !AuthService.IsPM() && state.entityPermissions.permission.read;
        case 'company-setup':
          return !AuthService.IsPM() && state.entityPermissions.tenant.update;
        case 'command-center':
          return false;
        case 'tenant-select':
          return state.userTenants.length > 1;
        case 'people':
          return !AuthService.IsPM() && state.entityPermissions.contact.read;
        default:
          return false;
      }
    });
  }, []);

  // tab state
  const [tab, setTab] = useState<DashboardTabs>('jobs');

  useEffect(() => {
    const splitPath = location.pathname.split('/');
    const currentTab = splitPath[3];
    if (tab !== currentTab) {
      setTab(currentTab as DashboardTabs);
    }
  }, [location.pathname]);

  // Effect to handle form mode from navigation state
  useEffect(() => {
    if (['edit', 'create', 'wizard'].includes(location?.state?.formMode)) {
      // if we have nav state, it will set the form mode and we don't want to reset it here
      return;
    }
    setFormMode('none');
  }, [location.state]);

  const getTabRoute = (tab: DashboardTabs): string => {
    let route: ROUTES;
    // start here after route config
    switch (tab) {
      case 'activity-feed':
        route = ROUTES.ACTIVITY_FEED;
        break;
      case 'jobs':
        route = ROUTES.JOBS;
        break;
      case 'properties':
        route = ROUTES.PROPERTIES;
        break;
      case 'property-groups':
        route = ROUTES.PROPERTY_GROUPS;
        break;
      case 'permissions':
        route = ROUTES.PERMISSIONS;
        break;
      case 'people':
        route = ROUTES.USERS;
        break;
      case 'user-profile':
        route = ROUTES.USER_PROFILE;
        break;
      case 'rule-config':
        route = ROUTES.VIOLATIONS;
        break;
      case 'company-setup':
        route = ROUTES.COMPANY_SETUP;
        break;
      case 'command-center':
        route = ROUTES.HEALTH;
        break;
      case 'tenant-select':
        route = ROUTES.TENANT_SELECT;
        break;
      case 'reports':
        route = ROUTES.REPORTS_JOBS_BY_PROPERTY;
        break;
      default:
        route = ROUTES.JOBS;
    }
    return routeWithParams(route, [state.tenant.id]);
  };
  const onNavigate = useCallback(() => {
    setFormMode('none');
  }, []);

  return (
    <VerticalTabLayout
      title={tab === 'permissions' ? '' : state.tenant.name}
      currentTab={tab}
      tabs={allowedTabs}
      getTabRoute={getTabRoute}
      isLoading={navigation.state === 'loading'}
      onNavigate={onNavigate}
    >
      <Outlet
        context={
          {
            entity: tenant,
            setEntity: setTenant,
            formMode,
            setFormMode,
            displayMode: 'tenant',
            properties,
            propertyGroups,
          } satisfies PanelRootContext<Tenant>
        }
      />
    </VerticalTabLayout>
  );
};

Component.displayName = 'Dashboard';
export default Component;
export const loader = dashboardLoader;
